import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  StatusOverview,
  Incident,
  UptimeStats,
  StatusSubscription,
  StaffUser,
  AlertRule,
  AlertInstance,
  SystemMetric,
  MetricHistory,
  SystemError,
  ApiResponse,
  PaginatedResponse,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8084/api/v1';

class StatusApiService {
  private publicApi: AxiosInstance;
  private staffApi: AxiosInstance;

  constructor() {
    // Public API (no auth required)
    this.publicApi = axios.create({
      baseURL: `${API_BASE_URL}/public`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Staff API (auth required)
    this.staffApi = axios.create({
      baseURL: `${API_BASE_URL}/admin`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to staff API requests
    this.staffApi.interceptors.request.use((config) => {
      const token = localStorage.getItem('staff_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.staffApi.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('staff_token');
          localStorage.removeItem('staff_user');
          window.location.href = '/staff/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ============================================
  // PUBLIC STATUS API (No Authentication)
  // ============================================

  async getStatus(): Promise<StatusOverview> {
    const response = await this.publicApi.get<StatusOverview>('/status');
    return response.data;
  }

  async getIncidents(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Incident>> {
    const response = await this.publicApi.get<PaginatedResponse<Incident>>('/status/incidents', {
      params,
    });
    return response.data;
  }

  async getIncident(id: string): Promise<Incident> {
    const response = await this.publicApi.get<Incident>(`/status/incidents/${id}`);
    return response.data;
  }

  async getUptime(): Promise<UptimeStats> {
    const response = await this.publicApi.get<UptimeStats>('/status/uptime');
    return response.data;
  }

  async subscribe(subscription: StatusSubscription): Promise<ApiResponse<void>> {
    const response = await this.publicApi.post<ApiResponse<void>>('/status/subscribe', subscription);
    return response.data;
  }

  async unsubscribe(token: string): Promise<ApiResponse<void>> {
    const response = await this.publicApi.post<ApiResponse<void>>('/status/unsubscribe', { token });
    return response.data;
  }

  // ============================================
  // STAFF AUTHENTICATION
  // ============================================

  async staffLogin(email: string, password: string): Promise<{ user: StaffUser; token: string }> {
    const response = await this.staffApi.post<{ user: StaffUser; token: string }>('/auth/login', {
      email,
      password,
    });
    return response.data;
  }

  async staffLogout(): Promise<void> {
    try {
      await this.staffApi.post('/auth/logout');
    } finally {
      localStorage.removeItem('staff_token');
      localStorage.removeItem('staff_user');
    }
  }

  async getStaffProfile(): Promise<StaffUser> {
    const response = await this.staffApi.get<StaffUser>('/auth/me');
    return response.data;
  }

  // ============================================
  // STAFF: ALERT MANAGEMENT
  // ============================================

  async getAlertRules(): Promise<AlertRule[]> {
    const response = await this.staffApi.get<AlertRule[]>('/alerts/rules');
    return response.data;
  }

  async createAlertRule(rule: Omit<AlertRule, 'id' | 'createdAt'>): Promise<AlertRule> {
    const response = await this.staffApi.post<AlertRule>('/alerts/rules', rule);
    return response.data;
  }

  async updateAlertRule(id: string, rule: Partial<AlertRule>): Promise<AlertRule> {
    const response = await this.staffApi.put<AlertRule>(`/alerts/rules/${id}`, rule);
    return response.data;
  }

  async deleteAlertRule(id: string): Promise<void> {
    await this.staffApi.delete(`/alerts/rules/${id}`);
  }

  async getActiveAlerts(): Promise<AlertInstance[]> {
    const response = await this.staffApi.get<AlertInstance[]>('/alerts/active');
    return response.data;
  }

  async acknowledgeAlert(id: string, note?: string): Promise<AlertInstance> {
    const response = await this.staffApi.post<AlertInstance>(`/alerts/${id}/acknowledge`, { note });
    return response.data;
  }

  async resolveAlert(id: string, note?: string): Promise<AlertInstance> {
    const response = await this.staffApi.post<AlertInstance>(`/alerts/${id}/resolve`, { note });
    return response.data;
  }

  // ============================================
  // STAFF: SYSTEM METRICS
  // ============================================

  async getSystemHealth(): Promise<{
    status: string;
    services: { name: string; status: string; latency?: number }[];
  }> {
    const response = await this.staffApi.get('/metrics/health');
    return response.data;
  }

  async getMetrics(params?: {
    type?: string;
    hours?: number;
  }): Promise<SystemMetric[]> {
    const response = await this.staffApi.get<SystemMetric[]>('/metrics/health', { params });
    return response.data;
  }

  async getMetricHistory(metricType: string, metricName: string, hours: number = 24): Promise<MetricHistory> {
    const response = await this.staffApi.get<MetricHistory>('/metrics/history', {
      params: { metric_type: metricType, metric_name: metricName, hours },
    });
    return response.data;
  }

  // ============================================
  // STAFF: ERROR TRACKING
  // ============================================

  async getErrors(params?: {
    severity?: string;
    resolved?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<SystemError>> {
    const response = await this.staffApi.get<PaginatedResponse<SystemError>>('/errors', { params });
    return response.data;
  }

  async getError(id: string): Promise<SystemError> {
    const response = await this.staffApi.get<SystemError>(`/errors/${id}`);
    return response.data;
  }

  async resolveError(id: string, note?: string): Promise<SystemError> {
    const response = await this.staffApi.post<SystemError>(`/errors/${id}/resolve`, { note });
    return response.data;
  }

  // ============================================
  // STAFF: INCIDENT MANAGEMENT
  // ============================================

  async createIncident(incident: {
    title: string;
    severity: string;
    affectedServices: string[];
    message: string;
  }): Promise<Incident> {
    const response = await this.staffApi.post<Incident>('/incidents', incident);
    return response.data;
  }

  async updateIncident(id: string, update: {
    status?: string;
    message: string;
  }): Promise<Incident> {
    const response = await this.staffApi.put<Incident>(`/incidents/${id}`, update);
    return response.data;
  }

  async resolveIncident(id: string, message: string): Promise<Incident> {
    const response = await this.staffApi.post<Incident>(`/incidents/${id}/resolve`, { message });
    return response.data;
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  setStaffToken(token: string): void {
    localStorage.setItem('staff_token', token);
  }

  getStaffToken(): string | null {
    return localStorage.getItem('staff_token');
  }

  setStaffUser(user: StaffUser): void {
    localStorage.setItem('staff_user', JSON.stringify(user));
  }

  getStaffUser(): StaffUser | null {
    const user = localStorage.getItem('staff_user');
    return user ? JSON.parse(user) : null;
  }

  isStaffAuthenticated(): boolean {
    return !!this.getStaffToken() && !!this.getStaffUser();
  }
}

export const statusApi = new StatusApiService();
export default statusApi;
